import { Button, PrimaryDialog, SearchBox, Typography } from "@foodpilot/foods";
import { Box, Divider, Stack, useTheme } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EntityQuitConfirmDialog } from "src/components/EntityForm";
import { ReferenceSearchDialog } from "src/components/ReferenceSearch";
import { IngredientFormIngredientCompany } from "../../../IngredientForm.types";
import { CompanyListGrid } from "./CompanyListGrid";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";
import { IngredientSupplierTransportFields } from "src/components/Transport/IngredientSupplierTransportFields";
import { getNextIngredientSupplierTransportStatus } from "src/components/Transport/ingredientSupplierTransportPersistence";
import type { IngredientSupplierTransportChangeReason } from "src/components/Transport/ingredientSupplierTransportTypes";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { getSupplierMutationPermission } from "../supplierMutationPermission";

type CompanyModalProps = {
    appearance?: "legacy" | "product-v2";
    isOpen: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    onValidate: (newSuppliers: IngredientFormIngredientCompany[]) => void;
    canModify?: boolean;
    existingIngredientSuppliers: IngredientFormIngredientCompany[];
    replacingSupplierIri?: IRI | null;
    selectionMode?: "multiple" | "single";
};
export const CompanyModal = (props: CompanyModalProps): ReactNode | null => {
    const { t } = useTranslation();
    const theme = useTheme();
    const featureProductUxV2 = useIsProductUxV2();
    const { selectedCompany } = useFoodpilotContext();
    const {
        appearance,
        canModify = true,
        existingIngredientSuppliers,
        isOpen,
        onValidate,
        replacingSupplierIri = null,
        selectionMode = "multiple",
        setOpen,
    } = props;
    const isProductUxV2 = appearance === "product-v2" || (appearance === undefined && featureProductUxV2);
    const search = useDelayedSearch();
    const initialDraft = useMemo(
        () =>
            existingIngredientSuppliers.filter(
                (ingredientSupplier) => ingredientSupplier.supplier["@id"] !== replacingSupplierIri,
            ),
        [existingIngredientSuppliers, replacingSupplierIri],
    );
    const [selectedIngredientSuppliers, setSelectedIngredientSuppliers] = useState(initialDraft);
    const [confirmRemovalOpen, setConfirmRemovalOpen] = useState(false);

    useEffect(() => {
        setSelectedIngredientSuppliers(initialDraft);
    }, [initialDraft, isOpen]);

    const finishClose = () => {
        setConfirmRemovalOpen(false);
        search.resetSearch();
        setOpen(false);
    };

    const cancel = () => {
        setSelectedIngredientSuppliers(initialDraft);
        finishClose();
    };

    const commitDraft = (draft = selectedIngredientSuppliers) => {
        onValidate(draft);
        finishClose();
    };

    const excludedSupplierIris = useMemo(
        () => new Set<IRI>(existingIngredientSuppliers.map((relation) => relation.supplier["@id"])),
        [existingIngredientSuppliers],
    );
    const selectSingleSupplier = (selection: IngredientFormIngredientCompany[]) => {
        const selectedSupplier = selection[0];
        if (selectedSupplier === undefined) return;

        const nextDraft = [...initialDraft, selectedSupplier];
        setSelectedIngredientSuppliers(nextDraft);
        if (replacingSupplierIri !== null) {
            setConfirmRemovalOpen(true);
            return;
        }

        commitDraft(nextDraft);
    };

    const originalIds = existingIngredientSuppliers.map((relation) => relation.supplier["@id"]);
    const draftIds = selectedIngredientSuppliers.map((relation) => relation.supplier["@id"]);
    const removesExistingSupplier = originalIds.some((supplierIri) => !draftIds.includes(supplierIri));

    const validate = () => {
        if (removesExistingSupplier) {
            setConfirmRemovalOpen(true);
            return;
        }

        commitDraft();
    };

    const updateTransportSelections = (
        supplierIri: IRI,
        transportSelections: IngredientFormIngredientCompany["transportSelections"],
        reason: IngredientSupplierTransportChangeReason,
    ) => {
        setSelectedIngredientSuppliers((current) =>
            current.map((row) =>
                row.supplier["@id"] === supplierIri ?
                    {
                        ...row,
                        transportSelections,
                        transportSelectionsStatus: getNextIngredientSupplierTransportStatus(
                            row.transportSelectionsStatus,
                            reason,
                        ),
                    }
                :   row,
            ),
        );
    };

    if (!isProductUxV2) {
        return (
            <>
                <PrimaryDialog
                    open={isOpen}
                    setOpen={setOpen}
                    options={{
                        title: (
                            <Typography variant="h2">
                                {t("ingredient.form.suppliermodal.title", "Add a supplier")}
                            </Typography>
                        ),
                        content: (
                            <Box component="div" sx={{ minHeight: theme.spacing(25), overflowX: "hidden" }}>
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    sx={{ padding: theme.spacing(3) }}
                                >
                                    <SearchBox
                                        variant="standalone"
                                        value={search.search}
                                        onChange={search.setSearch}
                                        placeholder={t("rechercher_un_fournisseur")}
                                    />
                                </Stack>
                                <CompanyListGrid
                                    appearance="legacy"
                                    currentIngredientSuppliers={
                                        selectionMode === "single" ? [] : selectedIngredientSuppliers
                                    }
                                    setNewIngredientsSuppliers={
                                        selectionMode === "single" ? selectSingleSupplier : (
                                            setSelectedIngredientSuppliers
                                        )
                                    }
                                    canModify={canModify}
                                    delayedSearch={search.delayedSearch}
                                    excludedSupplierIris={selectionMode === "single" ? excludedSupplierIris : undefined}
                                />
                                {selectedIngredientSuppliers.length > 0 && (
                                    <Stack gap={theme.spacing(2)} sx={{ p: theme.spacing(3) }}>
                                        <Divider />
                                        {selectedIngredientSuppliers.map((ingredientSupplier) => (
                                            <IngredientSupplierTransportFields
                                                appearance="legacy"
                                                disabled={getSupplierMutationPermission(canModify) !== "allowed"}
                                                key={ingredientSupplier.supplier["@id"]}
                                                ingredientSupplierId={extractIdFromIri(ingredientSupplier["@id"])}
                                                value={ingredientSupplier.transportSelections}
                                                onChange={(next, reason) =>
                                                    updateTransportSelections(
                                                        ingredientSupplier.supplier["@id"],
                                                        next,
                                                        reason,
                                                    )
                                                }
                                            />
                                        ))}
                                    </Stack>
                                )}
                            </Box>
                        ),
                    }}
                    onValidate={() => commitDraft()}
                    onCancel={cancel}
                    confirmAction={
                        removesExistingSupplier ?
                            {
                                title: (
                                    <Typography variant="h3">
                                        {t(
                                            "ingredientsupplier.confirmactiontitle",
                                            "Warning! You are about to remove suppliers",
                                        )}
                                    </Typography>
                                ),
                                content: (
                                    <Typography variant="body-medium">
                                        {t(
                                            "ingredientsupplier.confirmactioncontent",
                                            "All data associated with these suppliers will be lost.",
                                        )}
                                    </Typography>
                                ),
                                cancelText: t(
                                    "ingredientsupplier.confirmactioncancel",
                                    "No, I return to the suppliers selection",
                                ),
                                validateText: t(
                                    "ingredientsupplier.confirmactionvalidate",
                                    "Yes, I confirm that I wish to remove these suppliers",
                                ),
                            }
                        :   undefined
                    }
                    cancelText={t("primarydialog.cancel", "Cancel")}
                    validateText={t("primarydialog.validate", "Confirm")}
                />

                <EntityQuitConfirmDialog
                    open={confirmRemovalOpen}
                    title={t("ingredientsupplier.confirmactiontitle", "Warning! You are about to remove suppliers")}
                    content={t(
                        "ingredientsupplier.confirmactioncontent",
                        "All data associated with these suppliers will be lost.",
                    )}
                    cancelLabel={t("ingredientsupplier.confirmactioncancel", "No, I return to the suppliers selection")}
                    confirmLabel={t(
                        "ingredientsupplier.confirmactionvalidate",
                        "Yes, I confirm that I wish to remove these suppliers",
                    )}
                    onCancel={() => setConfirmRemovalOpen(false)}
                    onConfirm={() => commitDraft()}
                />
            </>
        );
    }

    return (
        <>
            <ReferenceSearchDialog
                closeLabel={t("common.close", "Close")}
                onClose={cancel}
                onSearchChange={search.setSearch}
                open={isOpen}
                searchPlaceholder={t("rechercher_un_fournisseur", "Search for a supplier in your repository...")}
                searchValue={search.search}
                footer={
                    selectionMode === "single" ? undefined : (
                        <Stack
                            alignItems="center"
                            direction="row"
                            flexShrink={0}
                            gap={theme.spacing(1)}
                            height="100%"
                            justifyContent="flex-end"
                            paddingX={theme.spacing(2)}
                            sx={{
                                borderTop: `${theme.custom.strokes.containerDivider.width} ${theme.custom.strokes.containerDivider.style} ${theme.custom.strokes.containerDivider.color}`,
                                boxSizing: "border-box",
                            }}
                        >
                            <Button variant="text" onClick={cancel}>
                                {t("primarydialog.cancel", "Cancel")}
                            </Button>
                            <Button variant="primary" onClick={validate}>
                                {t("primarydialog.validate", "Confirm")}
                            </Button>
                        </Stack>
                    )
                }
                title={
                    replacingSupplierIri === null ?
                        t("ingredient.form.suppliermodal.title", "Add a supplier")
                    :   t("ingredient.form.suppliermodal.replaceTitle", "Replace supplier")
                }
            >
                <CompanyListGrid
                    appearance="product-v2"
                    canModify={canModify}
                    currentIngredientSuppliers={selectionMode === "single" ? [] : selectedIngredientSuppliers}
                    setNewIngredientsSuppliers={
                        selectionMode === "single" ? selectSingleSupplier : setSelectedIngredientSuppliers
                    }
                    delayedSearch={search.delayedSearch}
                    excludedSupplierIris={selectionMode === "single" ? excludedSupplierIris : undefined}
                />
            </ReferenceSearchDialog>

            <EntityQuitConfirmDialog
                open={confirmRemovalOpen}
                title={t("ingredientsupplier.confirmactiontitle", "Warning! You are about to remove suppliers")}
                content={t(
                    "ingredientsupplier.confirmactioncontent",
                    "All data associated with these suppliers will be lost.",
                )}
                cancelLabel={t("ingredientsupplier.confirmactioncancel", "No, I return to the suppliers selection")}
                confirmLabel={t(
                    "ingredientsupplier.confirmactionvalidate",
                    "Yes, I confirm that I wish to remove these suppliers",
                )}
                onCancel={() => setConfirmRemovalOpen(false)}
                onConfirm={() => commitDraft()}
            />
        </>
    );
};

const extractIdFromIri = (iri?: IRI): number | undefined => {
    const rawId = iri?.split("/").pop();
    const id = rawId === undefined ? Number.NaN : Number.parseInt(rawId, 10);
    return Number.isNaN(id) ? undefined : id;
};
